import { z } from 'zod';

import type {
  NotificationChannel,
  ProviderKey,
  ProviderKeyInput,
  ProviderName,
} from '~/modules/query/notification/types';

/** The credential keys each provider's service client reads (the service's Logic provider clients). */
export const PROVIDER_CREDENTIAL_KEYS: Record<ProviderName, string[]> = {
  sendgrid: ['authToken'],
  twilio: ['accountSid', 'authToken'],
  plivo: ['authId', 'authToken'],
  telnyx: ['authToken'],
};

/** Browser is absent: the service has no browser provider client, so a new key cannot target it. */
export const PROVIDERS_BY_CHANNEL: { email: ['sendgrid']; sms: ['twilio', 'plivo', 'telnyx'] } = {
  email: ['sendgrid'],
  sms: ['twilio', 'plivo', 'telnyx'],
};

export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  browser: 'Browser',
};

const PROVIDER_LABELS: Record<ProviderName, string> = {
  sendgrid: 'SendGrid',
  twilio: 'Twilio',
  plivo: 'Plivo',
  telnyx: 'Telnyx',
};

const CREDENTIAL_LABELS: Record<string, string> = {
  authToken: 'Auth token',
  accountSid: 'Account SID',
  authId: 'Auth ID',
};

/** The placeholder over a saved credential: it says one is stored, never what it is. */
export const SAVED_CREDENTIAL_MASK = '••••••••';

export const isProviderName = (provider: string): provider is ProviderName =>
  Object.hasOwn(PROVIDER_CREDENTIAL_KEYS, provider);

/** The providers a new key can pick for a channel; none for browser. */
export const providersForChannel = (channel: NotificationChannel): readonly string[] =>
  channel === 'email' || channel === 'sms' ? PROVIDERS_BY_CHANNEL[channel] : [];

/** True when the create form offers this channel/provider pair; other stored rows edit with the provider read-only. */
export const isSupportedProvider = (channel: NotificationChannel, provider: string): boolean =>
  providersForChannel(channel).includes(provider);

export const providerLabel = (provider: string): string =>
  isProviderName(provider)
    ? PROVIDER_LABELS[provider]
    : provider.charAt(0).toUpperCase() + provider.slice(1);

/** "Twilio SMS": what a person calls the key, in a title or a confirm. */
export const providerKeyTitle = (key: Pick<ProviderKey, 'channel' | 'provider'>): string =>
  `${providerLabel(key.provider)} ${CHANNEL_LABELS[key.channel] ?? key.channel}`;

export const credentialLabel = (key: string): string =>
  CREDENTIAL_LABELS[key] ??
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

/**
 * A revealed payload as label/value rows: one per key of a flat JSON object, or the whole string
 * as one row when it is anything else.
 */
export function revealedCredentialRows(raw: string): { label: string; value: string }[] {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const rows = Object.entries(parsed)
        .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
        .map(([key, value]) => ({ label: credentialLabel(key), value }));
      if (rows.length > 0) return rows;
    }
  } catch {
    // Not JSON: shown whole below.
  }
  return [{ label: 'Credentials', value: raw }];
}

/** The credential fields to show: the provider's keys, none for a provider the form does not know. */
export function credentialKeys(provider: string): string[] {
  return isProviderName(provider) ? PROVIDER_CREDENTIAL_KEYS[provider] : [];
}

/**
 * True when the save leaves the stored credentials alone: an edit of the same provider with every
 * credential field blank. Typing any field replaces the set, so every field is then required.
 */
export function keepsStoredCredentials(
  form: Pick<ProviderKeyFormValues, 'provider' | 'credentials'>,
  stored?: ProviderKey,
): boolean {
  if (!stored || stored.provider !== form.provider) return false;
  const keys = credentialKeys(form.provider);
  return (
    (stored.hasCredentials || keys.length === 0) &&
    keys.every((key) => !form.credentials[key]?.trim())
  );
}

export interface ProviderKeyFormValues {
  channel: NotificationChannel;
  provider: string;
  /** What the person typed per key. Never seeded: reads carry no credentials, and all blank means "keep". */
  credentials: Record<string, string>;
  customerId: string;
  channelKey: string;
  isDefault: boolean;
  defaultFrom: string;
  priority: string;
  isActive: boolean;
}

export function emptyProviderKeyForm(): ProviderKeyFormValues {
  return {
    channel: 'email',
    provider: PROVIDERS_BY_CHANNEL.email[0],
    credentials: {},
    customerId: '',
    channelKey: '',
    isDefault: true,
    defaultFrom: '',
    priority: '0',
    isActive: true,
  };
}

export function providerKeyToForm(key: ProviderKey): ProviderKeyFormValues {
  return {
    channel: key.channel,
    provider: key.provider,
    credentials: {},
    customerId: key.customerId ?? '',
    channelKey: key.channelKey ?? '',
    isDefault: key.isDefault,
    defaultFrom: key.defaultFrom ?? '',
    priority: String(key.priority ?? 0),
    isActive: key.isActive,
  };
}

/** Mirrors the service's `normalizePhoneNumber` (US-centric E.164). */
export function isValidPhone(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith('1')) return true;
  return raw.trim().startsWith('+') && digits.length >= 11;
}

const DOMAIN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/** The DOM-order field names a failed check can name; credential fields are `credential-<key>`. */
export type ProviderKeyErrors = Record<string, string>;

/** The service's checks, run before the request. `stored` decides whether blank credentials may keep. */
export function validateProviderKeyForm(
  form: ProviderKeyFormValues,
  stored?: ProviderKey,
): ProviderKeyErrors {
  const errors: ProviderKeyErrors = {};

  if (!form.provider.trim()) errors.provider = 'Choose a provider.';

  if (!keepsStoredCredentials(form, stored)) {
    for (const key of credentialKeys(form.provider)) {
      if (!form.credentials[key]?.trim()) {
        errors[`credential-${key}`] = `Enter the ${credentialLabel(key).toLowerCase()}.`;
      }
    }
  }

  const channelKey = form.channelKey.trim();
  if (!form.isDefault) {
    if (!channelKey) {
      errors.channelKey = 'Enter a channel key, or make this the default provider.';
    } else if (form.channel === 'sms' && !isValidPhone(channelKey)) {
      errors.channelKey = 'Enter a valid phone number, such as +15555550123.';
    } else if (form.channel === 'email' && !DOMAIN.test(channelKey.toLowerCase())) {
      errors.channelKey = 'Enter a domain, such as mail.example.com.';
    }
  }

  const defaultFrom = form.defaultFrom.trim();
  if (form.channel === 'sms' && defaultFrom && !isValidPhone(defaultFrom)) {
    errors.defaultFrom = 'Enter a valid phone number, such as +15555550123.';
  }

  const priority = form.priority.trim();
  if (priority && !/^\d+$/.test(priority)) {
    errors.priority = 'Enter a whole number, 0 or higher.';
  }

  return errors;
}

/** The checks as a schema for `useFormValidation`; its issues are keyed by the same field names. */
export const providerKeySchema = (stored?: ProviderKey): z.ZodType =>
  z.unknown().superRefine((value, ctx) => {
    const errors = validateProviderKeyForm(value as ProviderKeyFormValues, stored);
    for (const [field, message] of Object.entries(errors)) {
      ctx.addIssue({ code: 'custom', path: [field], message });
    }
  });

/**
 * The PUT/POST body. All-blank credentials on an edit are left out, so the service keeps the sealed
 * value; otherwise they go out whole, as typed. A default key carries no channelKey.
 */
export function toProviderKeyInput(
  form: ProviderKeyFormValues,
  stored?: ProviderKey,
): ProviderKeyInput {
  const credentials = keepsStoredCredentials(form, stored)
    ? undefined
    : JSON.stringify(
        Object.fromEntries(
          credentialKeys(form.provider).map((key) => [key, form.credentials[key]?.trim() ?? '']),
        ),
      );

  return {
    channel: form.channel,
    provider: form.provider,
    ...(credentials === undefined ? {} : { credentials }),
    customerId: form.customerId.trim() || null,
    channelKey: form.isDefault ? null : form.channelKey.trim() || null,
    isDefault: form.isDefault,
    defaultFrom: form.defaultFrom.trim() || null,
    priority: Number(form.priority.trim() || '0'),
    isActive: form.isActive,
  };
}

export interface ProviderKeyFilters {
  q: string;
  channel: string;
  provider: string;
  /** `active`, `inactive` or empty for both. */
  active: string;
}

/** The list's search and filters; the search never looks at credentials. */
export function matchesProviderKeyFilters(key: ProviderKey, filters: ProviderKeyFilters): boolean {
  if (filters.channel && key.channel !== filters.channel) return false;
  if (filters.provider && key.provider !== filters.provider) return false;
  if (filters.active === 'active' && !key.isActive) return false;
  if (filters.active === 'inactive' && key.isActive) return false;

  const q = filters.q.trim().toLowerCase();
  if (!q) return true;
  return [key.provider, key.channel, key.customerId, key.channelKey, key.defaultFrom].some(
    (value) => value?.toLowerCase().includes(q),
  );
}
